/**
 * RepeatableInstanceList — EXISTING/NEW table sections (#1154)
 * Modeled on RackConfigurationList (#995).
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import {
  RepeatableInstanceRow,
  RepeatableInstanceTableHeader,
  type RepeatableTableColumnAlign,
} from './RepeatableInstanceRow';
import { groupByStatus } from './repeatableSectionUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { colors, spacing } from '../../theme';
import { FormCompactPrimaryButton } from '../forms/FormCompactPrimaryButton';
import { Plus, AlertCircle, Package } from '../icons';
import {
  resolveRecordTableLayout,
  resolveRecordTableReorderActive,
  useRecordTableSectionReorder,
} from '../recordTable';
import { repeatableSectionShowDragColumn } from '../recordTable/repeatableRowChrome';

import type {
  RepeatableInstance,
  RepeatableListSection,
  RepeatableTableLayout,
} from './types';

export interface RepeatableInstanceListProps<T extends RepeatableInstance> {
  instances: T[];
  entityPrefix: string;
  entityLabelPlural: string;
  addButtonLabel: string;
  emptyStateTitle: string;
  columnsForLayout: (layout: RepeatableTableLayout) => readonly string[];
  columnAlign?: RepeatableTableColumnAlign;
  headerLabel: (column: string, layout: RepeatableTableLayout) => string;
  formatCell: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string;
  getColumnSubline?: (
    instance: T,
    column: string,
    layout: RepeatableTableLayout,
  ) => string | undefined;
  columnUsesDoubleLine?: (
    column: string,
    layout: RepeatableTableLayout,
  ) => boolean;
  onInstancePress: (instance: T) => void;
  onDuplicateInstance?: (instance: T) => void;
  onDeleteInstance?: (instance: T) => void;
  canDeleteInstance?: (instance: T) => boolean;
  onAddInstance: () => void;
  onReorderSection?: (
    section: RepeatableListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  isLoading?: boolean;
  error?: Error | null;
  disabled?: boolean;
  readOnly?: boolean;
  allowReadOnlyOpen?: boolean;
  testID?: string;
}

interface InstanceSectionProps<T extends RepeatableInstance> {
  title: string;
  section: RepeatableListSection;
  instances: T[];
  showPlaceholder?: boolean;
  listProps: Omit<
    RepeatableInstanceListProps<T>,
    | 'instances'
    | 'isLoading'
    | 'error'
    | 'onAddInstance'
    | 'onInstancePress'
    | 'onReorderSection'
  >;
  onInstancePress: (instance: T) => void;
  onAddInstance: () => void;
  onReorderSection?: (
    section: RepeatableListSection,
    fromIndex: number,
    toIndex: number,
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  allowReadOnlyOpen?: boolean;
  testIDPrefix: string;
  tableLayout: RepeatableTableLayout;
  hideSectionTitle?: boolean;
}

function buildPlaceholderInstance(entityPrefix: string): RepeatableInstance {
  return {
    id: 0,
    instanceStatus: 'new',
    instanceName: entityPrefix,
    displayOrder: 0,
  };
}

function InstanceSection<T extends RepeatableInstance>({
  title,
  section,
  instances,
  showPlaceholder = false,
  listProps,
  onInstancePress,
  onAddInstance,
  onReorderSection,
  disabled,
  readOnly = false,
  allowReadOnlyOpen = false,
  testIDPrefix,
  tableLayout,
  hideSectionTitle = false,
}: InstanceSectionProps<T>): React.ReactElement {
  const rowCount = instances.length + (showPlaceholder ? 1 : 0);
  const isCompact = tableLayout === 'compact';
  const reorderActive = resolveRecordTableReorderActive({
    readOnly,
    onReorderSection,
    itemCount: instances.length,
  });
  const showDragColumn = repeatableSectionShowDragColumn(reorderActive);

  const {
    draggedIndex,
    dragOverIndex,
    registerRowLayout,
    registerRowRemeasure,
    handleDragHandleStart,
    handleDragHandleMove,
    handleDragHandleEnd,
    handleRowDragOver,
    handleRowDrop,
  } = useRecordTableSectionReorder({
    section,
    onReorderSection,
  });
  const columns = listProps.columnsForLayout(tableLayout);
  const placeholder = buildPlaceholderInstance(listProps.entityPrefix);

  return (
    <View style={styles.section}>
      {hideSectionTitle ? null : (
        <Text style={styles.sectionTitle}>{title}</Text>
      )}
      {rowCount > 0 ? (
        <View style={styles.recordTableCard}>
          <RepeatableInstanceTableHeader
            columns={columns}
            columnAlignMap={listProps.columnAlign}
            headerLabel={listProps.headerLabel}
            reorderEnabled={reorderActive}
            showDragColumn={showDragColumn}
            tableLayout={tableLayout}
            readOnly={readOnly}
            hasOpenAction={
              Boolean(onInstancePress) &&
              !disabled &&
              (!readOnly || allowReadOnlyOpen)
            }
          />
          {instances.map((instance, index) => (
            <RepeatableInstanceRow
              key={instance.id ?? `${title}-${instance.displayOrder}`}
              instance={instance}
              entityPrefix={listProps.entityPrefix}
              columns={columns}
              columnAlign={listProps.columnAlign}
              headerLabel={listProps.headerLabel}
              formatCell={listProps.formatCell}
              getColumnSubline={listProps.getColumnSubline}
              columnUsesDoubleLine={listProps.columnUsesDoubleLine}
              onPress={onInstancePress}
              onDuplicate={listProps.onDuplicateInstance}
              onDelete={
                listProps.onDeleteInstance &&
                listProps.canDeleteInstance?.(instance)
                  ? listProps.onDeleteInstance
                  : undefined
              }
              disabled={disabled}
              readOnly={readOnly}
              allowReadOnlyOpen={allowReadOnlyOpen}
              isLastInTable={index === rowCount - 1}
              testID={`${testIDPrefix}-row-${instance.id}`}
              tableLayout={tableLayout}
              reorderEnabled={reorderActive}
              rowIndex={index}
              isDragging={draggedIndex === index}
              isDragOver={dragOverIndex === index}
              onDragHandleStart={handleDragHandleStart}
              onDragHandleMove={handleDragHandleMove}
              onDragHandleEnd={handleDragHandleEnd}
              onRowDragOver={handleRowDragOver}
              onRowDrop={handleRowDrop}
              onRowLayout={registerRowLayout}
              registerRowRemeasure={registerRowRemeasure}
            />
          ))}
          {showPlaceholder ? (
            <RepeatableInstanceRow
              instance={placeholder as T}
              entityPrefix={listProps.entityPrefix}
              columns={columns}
              columnAlign={listProps.columnAlign}
              headerLabel={listProps.headerLabel}
              formatCell={listProps.formatCell}
              isPlaceholder
              isLastInTable
              tableLayout={tableLayout}
              testID={`${testIDPrefix}-placeholder-row`}
            />
          ) : null}
        </View>
      ) : null}
      {section === 'new' ? (
        <View
          style={[styles.addButtonRow, isCompact && styles.addButtonRowCompact]}
        >
          <AddInstanceButton
            label={listProps.addButtonLabel}
            onPress={onAddInstance}
            disabled={disabled || readOnly}
            testID={`${testIDPrefix}-add-button`}
            variant={isCompact ? 'compact' : 'primary'}
          />
        </View>
      ) : null}
    </View>
  );
}

interface AddInstanceButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID: string;
  variant?: 'primary' | 'compact';
  style?: StyleProp<ViewStyle>;
}

const AddInstanceButton: React.FC<AddInstanceButtonProps> = ({
  label,
  onPress,
  disabled,
  testID,
  variant = 'primary',
  style,
}) => {
  if (variant === 'compact') {
    return (
      <TouchableOpacity
        testID={testID}
        style={[
          styles.addCompactButton,
          disabled && styles.addCompactButtonDisabled,
          style,
        ]}
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label}
        activeOpacity={disabled ? 1 : 0.88}
      >
        <View style={styles.addCompactIconWrap}>
          <Plus size={14} color={colors.primary} strokeWidth={2.5} />
        </View>
        <Text style={styles.addCompactLabel}>{label}</Text>
      </TouchableOpacity>
    );
  }

  return (
    <FormCompactPrimaryButton
      testID={testID}
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={label}
      icon={<Plus size={14} color={colors.textLight} />}
      style={style}
    >
      {label}
    </FormCompactPrimaryButton>
  );
};

interface EmptyStateProps {
  title: string;
  addButtonLabel: string;
  onAddInstance: () => void;
  disabled?: boolean;
  testID: string;
  addButtonVariant?: 'primary' | 'compact';
}

const InstanceEmptyState: React.FC<EmptyStateProps> = ({
  title,
  addButtonLabel,
  onAddInstance,
  disabled,
  testID,
  addButtonVariant = 'primary',
}) => (
  <View testID={`${testID}-empty`} style={styles.emptyContainer}>
    <View style={styles.emptyContent}>
      <View style={styles.emptyIconWrap} accessibilityElementsHidden>
        <Package size={48} color={colors.textMuted} />
      </View>
      <Text style={styles.emptyTitle}>{title}</Text>
      <View style={styles.emptyAddButtonWrap}>
        <AddInstanceButton
          label={addButtonLabel}
          onPress={onAddInstance}
          disabled={disabled}
          testID={`${testID}-empty-add-button`}
          variant={addButtonVariant}
          style={styles.emptyAddButton}
        />
      </View>
    </View>
  </View>
);

export function RepeatableInstanceList<T extends RepeatableInstance>({
  instances,
  onInstancePress,
  onDuplicateInstance,
  onDeleteInstance,
  canDeleteInstance,
  onAddInstance,
  onReorderSection,
  isLoading = false,
  error = null,
  disabled = false,
  readOnly = false,
  allowReadOnlyOpen = false,
  testID = 'repeatable-instance-list',
  ...listConfig
}: RepeatableInstanceListProps<T>): React.ReactElement {
  const { dimensions } = useResponsive();
  const tableLayout: RepeatableTableLayout = resolveRecordTableLayout(
    dimensions.width,
  );
  const isCompactLayout = tableLayout === 'compact';

  const grouped = useMemo(() => groupByStatus(instances), [instances]);
  const hasExisting = grouped.existing.length > 0;
  const hasNew = grouped.new.length > 0;
  const showNewPlaceholder =
    !hasNew && (Platform.OS !== 'web' || isCompactLayout);

  if (isLoading) {
    return (
      <View testID={`${testID}-loading`} style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>
          Loading {listConfig.entityLabelPlural.toLowerCase()}...
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View testID={`${testID}-error`} style={styles.errorContainer}>
        <AlertCircle size={24} color={colors.error} />
        <Text style={styles.errorText}>
          Failed to load {listConfig.entityLabelPlural.toLowerCase()}
        </Text>
      </View>
    );
  }

  if (instances.length === 0) {
    const emptyShell = (
      <InstanceEmptyState
        title={listConfig.emptyStateTitle}
        addButtonLabel={listConfig.addButtonLabel}
        onAddInstance={onAddInstance}
        disabled={disabled || readOnly}
        testID={testID}
        addButtonVariant={isCompactLayout ? 'compact' : 'primary'}
      />
    );

    if (isCompactLayout) {
      return (
        <ScrollView
          testID={testID}
          style={styles.container}
          contentContainerStyle={styles.emptyScrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {emptyShell}
        </ScrollView>
      );
    }

    return (
      <View testID={testID} style={styles.container}>
        {emptyShell}
      </View>
    );
  }

  const sectionListProps = {
    ...listConfig,
    onDuplicateInstance,
    onDeleteInstance,
    canDeleteInstance,
  };

  return (
    <ScrollView
      testID={testID}
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
    >
      {hasExisting ? (
        <InstanceSection
          title="EXISTING"
          section="existing"
          instances={grouped.existing}
          listProps={sectionListProps}
          onInstancePress={onInstancePress}
          onAddInstance={onAddInstance}
          onReorderSection={onReorderSection}
          disabled={disabled}
          readOnly={readOnly}
          allowReadOnlyOpen={allowReadOnlyOpen}
          testIDPrefix={`${testID}-existing`}
          tableLayout={tableLayout}
        />
      ) : null}

      <InstanceSection
        title="NEW"
        section="new"
        instances={grouped.new}
        hideSectionTitle={!hasNew}
        showPlaceholder={showNewPlaceholder}
        listProps={sectionListProps}
        onInstancePress={onInstancePress}
        onAddInstance={onAddInstance}
        onReorderSection={onReorderSection}
        disabled={disabled}
        readOnly={readOnly}
        allowReadOnlyOpen={allowReadOnlyOpen}
        testIDPrefix={`${testID}-new`}
        tableLayout={tableLayout}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: spacing.lg,
  },
  emptyScrollContent: {
    flexGrow: 1,
    paddingBottom: spacing.lg,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textMuted,
    letterSpacing: 0.7,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  recordTableCard: {
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      default: {},
      web: {
        minWidth: 0,
        overflow: 'visible' as const,
      },
    }),
  },
  loadingContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: spacing.sm,
  },
  errorContainer: {
    padding: spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.error,
    marginTop: spacing.sm,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
    minHeight: Platform.OS === 'web' ? 480 : 320,
  },
  emptyContent: {
    alignItems: 'center',
    gap: spacing.sm,
    width: '100%',
    maxWidth: 360,
  },
  emptyIconWrap: {
    marginBottom: spacing.xs,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  emptyAddButtonWrap: {
    marginTop: spacing.md,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  emptyAddButton: {
    alignSelf: 'center',
  },
  addButtonRow: {
    width: '100%',
    marginTop: spacing.md,
  },
  addButtonRowCompact: {
    marginTop: spacing.sm,
  },
  addCompactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    width: '100%',
    minHeight: 48,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: colors.textMuted,
    backgroundColor: colors.backgroundPrimary,
  },
  addCompactButtonDisabled: {
    opacity: 0.5,
  },
  addCompactIconWrap: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
  },
  addCompactLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary,
  },
});
